import { useEffect, useRef, useState } from 'react';
import { fetchStatisticsV2History } from 'api/statisticsV2Api';
import type {
  StatisticsV2HistoryParams,
  StatisticsV2HistoryProcess,
  StatisticsV2Pagination,
  StatisticsV2Task,
} from 'model/statisticsModel';

interface UseStatisticsV2HistoryResult {
  data: StatisticsV2HistoryProcess[] | StatisticsV2Task[];
  pagination: StatisticsV2Pagination | null;
  loading: boolean;
}

const useStatisticsV2History = (
  organizationId: number | null | undefined,
  params: StatisticsV2HistoryParams,
): UseStatisticsV2HistoryResult => {
  const [data, setData] = useState<StatisticsV2HistoryProcess[] | StatisticsV2Task[]>([]);
  const [pagination, setPagination] = useState<StatisticsV2Pagination | null>(null);
  const [loading, setLoading] = useState(false);
  // Serialize params to detect value-level changes without needing a stable object reference
  const paramsKey = JSON.stringify(params);
  const paramsKeyRef = useRef(paramsKey);
  paramsKeyRef.current = paramsKey;

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    setLoading(true);
    fetchStatisticsV2History(organizationId, JSON.parse(paramsKeyRef.current))
      .then((result) => {
        if (!cancelled) {
          setData(result.data);
          setPagination(result.pagination);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, paramsKey]);

  return { data, pagination, loading };
};

export default useStatisticsV2History;
